import { useContext, useMemo } from "react"

import isEqual from "lodash-es/isEqual"

import { DragSourceDataContext } from "shared/context/drag-and-drop/DragSourceDataContext"
import { usePointerPosition } from "shared/lib/drag-and-drop/usePointerPosition"
import { exists } from "shared/lib/exists"
import { useDebouncedState } from "shared/lib/useDebouncedState"
import { useEvent } from "shared/lib/useEvent"
import { WithID } from "shared/types/WithID"
import { DragSource } from "shared/types/drag-and-drop/DragSource"

import { DragMoveEvent, useDndMonitor } from "@dnd-kit/core"

import { useTreeState } from "../hooks/useTreeState"
import { isTableDragSource } from "../lib/isTableDragSource"
import { isTableDropTarget } from "../lib/isTableDropTarget"
import { TableProps } from "../types"
import { findIndexRelativeToParent } from "../utils/findIndexRelativeToParent"
import { findInsertionIndex } from "../utils/findInsertionIndex"
import { DragMoveContextType, TableDnDContextType } from "./TableDnDContext"

/**
 * Orchestration hook for table drag and drop using dnd-kit.
 * Reuses the existing findInsertionIndex logic and tree state management.
 */
export function useTableDnD<I extends WithID>(props: TableProps<I>) {
  const {
    items,
    maxDepth = 3,
    isItemCollapsed,
    canInsertInto,
    onItemPositionChange,
    availableDragTypes
  } = props

  const [dragMoveState, setDragMoveState, setDebouncedDragMoveState] =
    useDebouncedState<DragMoveContextType>({}, 0)

  const { dragSource: activeDragSource } = useContext(DragSourceDataContext)
  const { getPointerPosition } = usePointerPosition()

  const dragSource = useMemo(() => {
    return isTableDragSource(activeDragSource) ? activeDragSource : null
  }, [activeDragSource])

  const collapsedNodes = useMemo(() => {
    if (!isItemCollapsed) return []
    const collectCollapsed = (
      nodes: typeof items,
      acc: string[] = []
    ): string[] =>
      nodes.reduce((result, node) => {
        if (isItemCollapsed(node)) result.push(node.id)
        if (node.children) collectCollapsed(node.children, result)
        return result
      }, acc)
    return collectCollapsed(items)
  }, [items, isItemCollapsed])

  const { flattenedTree, onItemPositionChange: onInternalPositionChange } =
    useTreeState({
      tree: items,
      draggedElementID: dragSource?.id || null,
      collapsedNodes
    })

  const prevTreeIndex = useMemo(
    () => flattenedTree.findIndex(node => node.id === dragSource?.data.id),
    [flattenedTree, dragSource]
  )

  const handleDrop = useEvent((item: DragSource) => {
    const { nextParent, nextIndex } = dragMoveContext
    if (
      !item ||
      !isTableDragSource(item) ||
      flattenedTree.length === 0 ||
      nextParent === undefined ||
      nextIndex === undefined ||
      nextIndex === -1
    ) {
      return
    }
    let indexAdjustment = 0
    if (item.meta.parentID === nextParent && nextIndex > prevTreeIndex) {
      indexAdjustment -= 1
    }

    const resolvedPrevIndex = findIndexRelativeToParent(
      flattenedTree,
      dragSource?.meta.parentID ?? null,
      prevTreeIndex || 0
    )
    const resolvedNextIndex = Math.max(
      findIndexRelativeToParent(flattenedTree, nextParent, nextIndex) +
        indexAdjustment,
      0
    )

    onInternalPositionChange?.(
      item.data.id,
      resolvedPrevIndex,
      item.meta.parentID || null,
      resolvedNextIndex,
      nextParent || null
    )
    onItemPositionChange?.(
      item.data.id,
      resolvedPrevIndex,
      item.meta.parentID || null,
      resolvedNextIndex,
      nextParent || null
    )

    setDragMoveState({})
  })

  useDndMonitor({
    onDragMove: (event: DragMoveEvent) => {
      const { rect, data } = event.over || {}
      const pointerPosition = getPointerPosition()

      if (!isTableDropTarget(data?.current) || !pointerPosition || !rect) {
        handleDragMove({})
        return
      }

      const middleClientY = rect.top + rect.height / 2

      const insertBefore = pointerPosition.y < middleClientY

      handleDragMove({
        nextIndex: data.current.meta.index,
        insertBefore
      })
    },
    onDragEnd: () => {
      if (isTableDragSource(dragSource)) {
        handleDrop(dragSource)
      }
    }
  })

  const handleDragMove = useEvent((state: DragMoveContextType) => {
    if (!isEqual(state, dragMoveState)) {
      if (!dragMoveState.nextIndex) {
        setDragMoveState(state)
      }
      setDebouncedDragMoveState(state, 50)
    }
  })

  const dndContext = useMemo<TableDnDContextType<I>>(
    () => ({
      prevTreeIndex,
      onDragMove: handleDragMove,
      canInsertInto,
      availableDragTypes,
      dragSource: dragSource || null
    }),
    [
      dragSource,
      prevTreeIndex,
      handleDragMove,
      canInsertInto,
      availableDragTypes
    ]
  )

  const dragMoveContext: DragMoveContextType = useMemo(() => {
    const { index, parentID } = findInsertionIndex<I>({
      nodes: flattenedTree,
      insertBefore: !!dragMoveState.insertBefore,
      index: dragMoveState.nextIndex || 0,
      maxDepth,
      canInsertInto,
      dragSource: dragSource || null
    })

    return {
      insertBefore: !!dragMoveState.insertBefore,
      nextIndex: exists(dragMoveState.nextIndex) ? index : undefined,
      nextParent: exists(dragMoveState.nextIndex) ? parentID : null
    }
  }, [dragMoveState, flattenedTree, maxDepth, dragSource, canInsertInto])

  return {
    flattenedTree,
    dndContext,
    dragMoveContext,
    handleDrop
  }
}
